<script setup lang="ts">
const route = useRoute()
const {formatDate} = useDate()
const lp = useLocalePath()
const config = useRuntimeConfig()
const {
  public: {baseUrl},
} = useRuntimeConfig()

const fetchArticles = useArticles()
const slug = route.params.slug as string
const {data: rawArticle} = await useAsyncData(`article-${slug}`, () => fetchArticles.fetchBySlug(slug))
const {data: articles} = await useAsyncData('blog-page', () => fetchArticles.list())
const {data: article} = useLivePreview({initialData: rawArticle.value, depth: 2})

if (!rawArticle.value) {
  throw createError({
    statusCode: 404,
  })
}

const filteredArticles = computed(() => articles.value?.filter(a => a.slug !== slug) || [])

useSeoMeta({
  title: article.value?.title,
  description: article.value?.description,
  ogUrl: `${baseUrl}/blog/${slug}`,
  ogTitle: article.value?.title,
  ogDescription: article.value?.description,
  ogImage: resolveMediaUrl(article.value?.image),
  ogImageAlt: resolveMediaAlt(article.value?.image),
})

useSchemaOrg([
  defineArticle({
    headline: article.value?.title,
    thumbnailUrl: article.value?.image?.url as string,
    url: config.public.baseUrl + lp(`/blog/${article.value?.slug}`),
    image: article.value?.image?.url as string,
    datePublished: article.value?.createdAt,
    author() {
      const firstAuthor = article.value?.authors?.[0]
      return firstAuthor
        ? {
            name: firstAuthor.name,
            image: firstAuthor.image?.url,
          }
        : undefined
    },
  }),
])
</script>

<template>
  <main>
    <Container
      v-if="article"
      class="screen-content relative grid grid-cols-4 flex-col px-0 pt-24 sm:pt-32 lg:gap-8 lg:px-0"
    >
      <div class="col-span-full mb-8 flex items-start pl-8 md:pl-12 lg:col-span-1 lg:mt-3 lg:pl-16">
        <BackLink to="/blog">
          {{ $t('blog.back') }}
        </BackLink>
      </div>

      <header
        id="header"
        class="col-span-4 flex flex-col items-start gap-y-2 px-8 text-left md:px-12 lg:col-span-3 lg:pl-0 lg:pr-16"
      >
        <Heading as="h1" size="large">
          {{ article.title }}
        </Heading>
        <Paragraph size="large" class="mb-4">
          {{ article.description }}
        </Paragraph>

        <Paragraph class="!text-gray-600">
          <time class="flex items-center gap-1">
            <div v-if="article.category" class="flex items-center gap-1">
              <span class="font-medium text-main-500">{{ article.category.name }} </span>
              <span>—</span>
            </div>
            {{ formatDate(new Date(article.createdAt)) }}
          </time>
        </Paragraph>
      </header>

      <aside class="col-span-full flex w-full flex-col rounded-lg p-6 md:px-12 lg:col-span-1 lg:pl-16 lg:pr-0">
        <div v-for="author of article.authors || []" :key="author.id" class="flex gap-x-4">
          <NuxtImg
            v-if="author.image"
            v-bind="resolveMediaProps(author.image)"
            :width="128"
            :height="128"
            fit="cover"
            class="h-14 w-14 rounded-full object-cover bordershadow-gray"
          />
          <div class="flex flex-col justify-center gap-y-1">
            <Paragraph weight="bold">{{ author.name }}</Paragraph>
            <Paragraph>{{ author.role }}</Paragraph>
          </div>
        </div>
      </aside>

      <article class="col-span-4 max-lg:px-2 lg:col-span-3">
        <div v-if="article.showImage && article.image">
          <NuxtImg
            v-bind="resolveMediaProps(article.image)"
            :width="1200"
            :height="600"
            fit="cover"
            class="z-10 aspect-video h-full w-full rounded-lg bg-white object-cover bordershadow-lg-gray"
          />
        </div>

        <main class="w-full pb-16 max-md:px-6 md:px-12 lg:pl-0 lg:pr-16" :class="[{'pt-16': article.showImage}]">
          <RichText :raw="article.content" />
        </main>
      </article>
    </Container>

    <Container class="screen-content flex w-full flex-col items-center pb-8 max-sm:px-2 sm:px-10">
      <div class="my-8 flex w-full flex-col justify-start max-sm:px-8 sm:px-2">
        <Heading as="h3" size="small">{{ $t('blog.enjoy_article') }}</Heading>
        <Paragraph>{{ $t('blog.more_articles') }}</Paragraph>
      </div>

      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        <ArticlePreview
          v-for="previewArticle of filteredArticles.slice(0, 3)"
          :key="previewArticle.slug"
          :article="previewArticle"
          class="w-full"
        />
      </div>
    </Container>
  </main>
</template>
